import { z } from 'zod';

// =============================================================================
// Draft Schema
// =============================================================================

const draftSchema = z.object({
	id: z.string(),
	authorId: z.string(),
	status: z.enum(['active', 'inactive']),
	updatedAt: z.number(),
	type: z.enum(['user_draft', 'ai_draft']).nullish(),
});

// =============================================================================
// Pre-drafting input
// =============================================================================

/**
 * The starter's own input from the petition creation flow
 */
const userInputSchemaBase = z.object({
	intro: z.enum(['ai', 'user']),
	issuePrompt: z.string(),
	personalStory: z.string(),
	flow: z.enum(['chat']),
});

const userInputSchemaDraft = userInputSchemaBase.extend({
	intro: userInputSchemaBase.shape.intro.nullish(),
	issuePrompt: userInputSchemaBase.shape.issuePrompt.nullish(),
	personalStory: userInputSchemaBase.shape.personalStory.nullish(),
	flow: userInputSchemaBase.shape.flow.nullish(),
});

// =============================================================================
// AI-generated Content Schemas
// =============================================================================

const aiGeneratedContentSchemaBase = z.object({
	ask: z.string(),
	description: z.string(),
	image: z.object({ url: z.string(), type: z.enum(['AI_GENERATED']) }),
});

const aiGeneratedContentSchemaDraft = aiGeneratedContentSchemaBase.extend({
	ask: aiGeneratedContentSchemaBase.shape.ask.nullish(),
	description: aiGeneratedContentSchemaBase.shape.description.nullish(),
	image: aiGeneratedContentSchemaBase.shape.image.nullish(),
});

// =============================================================================
// Petition Content Schemas
// =============================================================================

/**
 * Petition content (user-created/modified)
 */
const petitionContentSchemaBase = z.object({
	ask: z.string().min(1),
	description: z.string().min(1),
	image: z.object({ url: z.string().min(1), type: z.enum(['USER_UPLOADED', 'AI_GENERATED']) }),
	reach: z.enum(['local', 'national', 'global']),
	location: z.object({ id: z.string().min(1), name: z.string().min(1) }),
	selectedDecisionMakerIds: z
		.array(z.string())
		.describe(
			'Petition target ids of the inferred decision makers the starter kept: every inferred id when inference finishes, minus any they remove. [] means none are kept; unset means inference never returned a result. Publish attaches exactly these, and any array (even []) tells petition_service DMI already ran.',
		),
});

const petitionContentSchemaDraft = petitionContentSchemaBase.extend({
	ask: petitionContentSchemaBase.shape.ask.nullish(),
	description: petitionContentSchemaBase.shape.description.nullish(),
	image: petitionContentSchemaBase.shape.image.nullish(),
	reach: petitionContentSchemaBase.shape.reach.nullish(),
	location: petitionContentSchemaBase.shape.location.nullish(),
	selectedDecisionMakerIds: petitionContentSchemaBase.shape.selectedDecisionMakerIds.nullish(),
});

const petitionContentSchemaPublish = petitionContentSchemaBase.extend({
	ask: petitionContentSchemaBase.shape.ask,
	description: petitionContentSchemaBase.shape.description,
	image: petitionContentSchemaBase.shape.image.nullish(),
	reach: petitionContentSchemaBase.shape.reach.nullish(),
	location: petitionContentSchemaBase.shape.location.nullish(),
	selectedDecisionMakerIds: petitionContentSchemaBase.shape.selectedDecisionMakerIds.nullish(),
});

// =============================================================================
// Editing Draft Schemas
// =============================================================================

/**
 * In-progress edits buffered while the user is on an edit step. Committed back
 * into `petitionDraft` only on validated submission, so partial/invalid input
 * can't pollute the canonical draft via blur or non-validating navigation.
 */
const editingDraftSchemaBase = z.object({
	ask: z.string(),
	description: z.string(),
});

const editingDraftSchemaDraft = editingDraftSchemaBase.extend({
	ask: editingDraftSchemaBase.shape.ask.nullish(),
	description: editingDraftSchemaBase.shape.description.nullish(),
});

// =============================================================================
// Metadata Schemas
// =============================================================================

const decisionMakerSchema = z.object({
	canonicalPetitionTargetId: z.string(),
	name: z.string(),
	position: z.string(),
	reasoning: z.string(),
});

/**
 * User context information
 */
const userContextSchema = z.object({
	id: z.string().optional(),
	loginOrJoinMethod: z.string().optional(),
	initialLoginState: z.enum(['AUTHENTICATED', 'IDENTIFIED', 'GUEST']).optional(),
});

/**
 * Common metadata across all draft types
 */
const draftMetadataSchemaBase = z.object({
	draftMode: z.string(),
	imageJobId: z.string(),
	imageFetchCount: z.number(),
	imageGenerationCount: z.number(),
	user: userContextSchema,
	variant: z.enum(['control', 'fake_control', 'chat_agent']),
	deviceId: z.string(),
	angleToken: z.string(),
	videoConsent: z.boolean(),
	inferredScopeLocation: z
		.object({
			scope: z.enum(['LOCAL', 'NATIONAL', 'GLOBAL']),
			location: z.string().nullable(),
			city: z.string().nullable(),
			adminArea: z.string().nullable(),
			countryCode: z.string().nullable(),
		})
		.describe(
			"Background scope/location inference result (CHANGE-97720), inferred when the issue prompt is submitted, for inference-vs-entered drift on activate_petition. city/adminArea/countryCode (CHANGE-98274) also feed the decision-maker inference request, which reads them back from the draft. Persisting them is a deliberate call: when source is GEOIP these derive from the starter's own IP rather than typed text, but the draft already carries far more sensitive content (the personal story itself).",
		),
	decisionMakers: z
		.object({
			supportedTargetCountries: z
				.array(z.string())
				.nullish()
				.describe(
					'Target countries decision makers are inferred for, copied from the sapDmiSupportedTargetCountries feature config on every page load (replaced, not merged) so the eligibility check, which runs when location inference settles, can read it from the draft.',
				),
			eligible: z
				.boolean()
				.nullish()
				.describe(
					'Whether the draft qualifies for decision-maker inference, set when location inference settles: a local target with a resolved city and admin area, in a supported target country. Unset until then, including when location inference fails, so the UI can tell "not known yet" from "not eligible".',
				),
			recommendations: z
				.array(decisionMakerSchema)
				.nullish()
				.describe(
					'Decision-maker inference (DMI) recommendations the service would assign that have a petition target id. Kept so the edit step can show them and publish can attach the same set, since the petition does not exist yet and inference is non-deterministic.',
				),
			classification: z
				.record(z.string(), z.unknown())
				.nullish()
				.describe(
					'The DMI classification from the same run as the recommendations, unset until inference returns. Publish sends it to petition_service, which stores it on the petition like the async DMI run does, since DMI is not run again there.',
				),
			loadingShown: z
				.boolean()
				.nullish()
				.describe(
					'Set the first time the edit step shows decision makers loading, so a failed or empty result then shows the empty message instead of nothing.',
				),
			carouselRenderedOnEdit: z
				.boolean()
				.nullish()
				.describe(
					'Set the first time the edit step renders the decision-maker cards, whether or not the starter scrolls down to them, so the preview step only shows decision makers once edit has shown them.',
				),
		})
		.describe(
			'Decision-maker inference state for the draft: its inputs, eligibility, results and what the edit step has shown.',
		),
});

const draftMetadataSchemaDraft = draftMetadataSchemaBase.extend({
	draftMode: draftMetadataSchemaBase.shape.draftMode.nullish(),
	imageJobId: draftMetadataSchemaBase.shape.imageJobId.nullish(),
	imageFetchCount: draftMetadataSchemaBase.shape.imageFetchCount.nullish(),
	imageGenerationCount: draftMetadataSchemaBase.shape.imageGenerationCount.nullish(),
	user: draftMetadataSchemaBase.shape.user.nullish(),
	variant: draftMetadataSchemaBase.shape.variant.nullish(),
	deviceId: draftMetadataSchemaBase.shape.deviceId.nullish(),
	angleToken: draftMetadataSchemaBase.shape.angleToken.nullish(),
	videoConsent: draftMetadataSchemaBase.shape.videoConsent.nullish(),
	inferredScopeLocation: draftMetadataSchemaBase.shape.inferredScopeLocation.nullish(),
	decisionMakers: draftMetadataSchemaBase.shape.decisionMakers.nullish(),
});

// =============================================================================
// Draft Schema
// =============================================================================

const petitionDraftSchema = draftSchema.extend({
	input: userInputSchemaDraft.nullish(),
	recommendedDraft: aiGeneratedContentSchemaDraft.nullish(),
	petitionDraft: petitionContentSchemaDraft.nullish(),
	editingDraft: editingDraftSchemaDraft.nullish(),
	metadata: draftMetadataSchemaDraft.nullish(),
});

// =============================================================================
// Publish Schema
// =============================================================================

export const publishPetitionSchema = z.object({
	recommendedDraft: aiGeneratedContentSchemaDraft.nullish(),
	petitionDraft: petitionContentSchemaPublish,
	metadata: draftMetadataSchemaDraft.pick({ decisionMakers: true }).nullish(),
});

// =============================================================================
// Post-draft Schemas - validates whether enough petition content is present
// to start mid-flow (preview/create steps)
// =============================================================================

export const postDraftUserSchema = z.object({
	type: draftSchema.shape.type,
	petitionDraft: petitionContentSchemaBase.pick({ ask: true, description: true }),
});

export const postDraftAiSchema = z.object({
	type: draftSchema.shape.type,
	petitionDraft: petitionContentSchemaBase.pick({ ask: true, description: true }),
	recommendedDraft: aiGeneratedContentSchemaBase.pick({ ask: true, description: true }),
});

// =============================================================================
// Type Exports
// =============================================================================

export type PetitionDraftSchema = z.infer<typeof petitionDraftSchema>;
